<template>
  <v-card class="mb-4" variant="outlined">
    <v-card-title class="bg-deep-purple-darken-1 text-white d-flex align-center py-2">
      <v-icon class="mr-2" size="small">mdi-chart-arc</v-icon>
      Trafic Inclus vs Consumat
      <v-spacer />
      <v-chip v-if="comparison && comparison.product_name && !comparison.error" color="white" variant="flat" size="small" class="text-deep-purple">
        {{ comparison.product_name }}
      </v-chip>
      <v-btn icon size="x-small" variant="text" color="white" @click="loadData" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-4">
      <v-progress-linear v-if="loading" indeterminate color="deep-purple" class="mb-2" />

      <v-alert v-if="errorMsg" type="warning" variant="tonal" density="compact" class="mb-3">
        {{ errorMsg }}
      </v-alert>

      <template v-if="!loading && !errorMsg && comparison && !comparison.error">
        <div class="text-caption text-grey mb-3">
          Perioada: {{ comparison.billing_period }}
        </div>

        <!-- Data usage -->
        <div class="mb-4">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-medium">
              <v-icon size="small" color="blue" class="mr-1">mdi-download</v-icon>
              Date
            </span>
            <span class="text-caption">
              {{ formatData(comparison.consumed.data_mb) }} /
              {{ comparison.unlimited.data ? 'Nelimitat' : formatData(comparison.included.data_mb) }}
            </span>
          </div>
          <v-progress-linear
            v-if="!comparison.unlimited.data"
            :model-value="Math.min(comparison.percentage.data || 0, 100)"
            :color="getBarColor(comparison.percentage.data)"
            bg-color="grey-lighten-3"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption font-weight-bold">{{ comparison.percentage.data }}%</span>
            </template>
          </v-progress-linear>
          <v-progress-linear
            v-else
            model-value="0"
            color="grey-lighten-2"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption">Nelimitat</span>
            </template>
          </v-progress-linear>
          <v-alert v-if="comparison.percentage.data > 100" type="error" density="compact" variant="tonal" class="mt-1 text-caption">
            Depasire: {{ (comparison.percentage.data - 100).toFixed(1) }}% peste limita
          </v-alert>
        </div>

        <!-- SMS usage -->
        <div class="mb-4">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-medium">
              <v-icon size="small" color="green" class="mr-1">mdi-message-text</v-icon>
              SMS
            </span>
            <span class="text-caption">
              {{ comparison.consumed.sms_count }} msg /
              {{ comparison.unlimited.sms ? 'Nelimitat' : comparison.included.sms_count + ' msg' }}
            </span>
          </div>
          <v-progress-linear
            v-if="!comparison.unlimited.sms"
            :model-value="Math.min(comparison.percentage.sms || 0, 100)"
            :color="getBarColor(comparison.percentage.sms)"
            bg-color="grey-lighten-3"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption font-weight-bold">{{ comparison.percentage.sms }}%</span>
            </template>
          </v-progress-linear>
          <v-progress-linear
            v-else
            model-value="0"
            color="grey-lighten-2"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption">Nelimitat</span>
            </template>
          </v-progress-linear>
          <v-alert v-if="comparison.percentage.sms > 100" type="error" density="compact" variant="tonal" class="mt-1 text-caption">
            Depasire: {{ (comparison.percentage.sms - 100).toFixed(1) }}% peste limita
          </v-alert>
        </div>

        <!-- Voice usage -->
        <div class="mb-2">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-medium">
              <v-icon size="small" color="orange" class="mr-1">mdi-phone</v-icon>
              Voce
            </span>
            <span class="text-caption">
              {{ formatMinutes(comparison.consumed.voice_minutes) }} /
              {{ comparison.unlimited.voice ? 'Nelimitat' : formatMinutes(comparison.included.voice_minutes) }}
            </span>
          </div>
          <v-progress-linear
            v-if="!comparison.unlimited.voice"
            :model-value="Math.min(comparison.percentage.voice || 0, 100)"
            :color="getBarColor(comparison.percentage.voice)"
            bg-color="grey-lighten-3"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption font-weight-bold">{{ comparison.percentage.voice }}%</span>
            </template>
          </v-progress-linear>
          <v-progress-linear
            v-else
            model-value="0"
            color="grey-lighten-2"
            height="22"
            rounded
          >
            <template v-slot:default>
              <span class="text-caption">Nelimitat</span>
            </template>
          </v-progress-linear>
          <v-alert v-if="comparison.percentage.voice > 100" type="error" density="compact" variant="tonal" class="mt-1 text-caption">
            Depasire: {{ (comparison.percentage.voice - 100).toFixed(1) }}% peste limita
          </v-alert>
        </div>

        <!-- Total cost -->
        <div class="text-right text-caption text-grey mt-3">
          Cost total: {{ formatCost(comparison.consumed.total_cost) }} EUR
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { getTrafficUsageComparison } from '@/composables/useTrafficAPI'

export default {
  name: 'TrafficUsageComparison',
  props: {
    simId: { type: [Number, String], required: true },
    period: { type: String, default: 'current_month' },
    token: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      errorMsg: null,
      comparison: null
    }
  },
  watch: {
    simId() { this.loadData() },
    period() { this.loadData() }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      if (!this.simId || !this.token) return
      this.loading = true
      this.errorMsg = null
      try {
        const result = await getTrafficUsageComparison(this.token, {
          simId: this.simId,
          period: this.period
        })
        if (result.error) {
          this.errorMsg = result.message
          this.comparison = null
        } else {
          this.comparison = result
        }
      } catch (e) {
        this.errorMsg = e.message
      } finally {
        this.loading = false
      }
    },
    formatData(mb) {
      if (!mb || mb === 0) return '0 MB'
      const val = Number(mb)
      if (val >= 1024 * 1024) return (val / (1024 * 1024)).toFixed(2) + ' TB'
      if (val >= 1024) return (val / 1024).toFixed(2) + ' GB'
      return val.toFixed(2) + ' MB'
    },
    formatMinutes(min) {
      if (!min || min === 0) return '0 min'
      const val = Number(min)
      if (val >= 60) return (val / 60).toFixed(1) + ' ore'
      return val.toFixed(1) + ' min'
    },
    formatCost(cost) {
      if (!cost || cost === 0) return '0.00'
      return Number(cost).toFixed(2)
    },
    getBarColor(percentage) {
      if (percentage == null) return 'grey'
      if (percentage > 90) return 'error'
      if (percentage > 70) return 'warning'
      return 'success'
    }
  }
}
</script>
